import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type {
  CatalogItemDto,
  LegacyItemPricesResponse,
  StockCardResponse,
  StockDocumentDto,
  StockMovementKind,
  StockOnHandResponse,
  StockValuationResponse,
} from '@hesabat/contracts';
import { LEGACY_DOCUMENT_LABELS } from '@hesabat/contracts';
import { api, legacyApi, ProblemError, UnauthorizedError } from './api';
import SearchSelect from './SearchSelect';
import { formatMoney, formatQuantity, isZero, parseAmount } from './money';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  /** الافتتاحى والجرد بصلاحية `journal.manual` — الإخفاء راحة، والـ API يفرضها. */
  canPost: boolean;
  onPosted: () => void;
  onOpenLedger: (accountCode: string) => void;
  onSessionLost: () => void;
}

type Tab = 'stock' | 'card' | 'count' | 'opening' | 'documents';

const KIND_LABELS: Record<StockMovementKind, string> = {
  opening: 'رصيد افتتاحى',
  purchase: 'شراء',
  sale: 'بيع',
  sale_return: 'مرتجع مبيعات',
  sale_cancellation: 'إلغاء فاتورة',
  adjustment: 'جرد وتسوية',
};

const ISSUE_LABELS: Record<string, string> = {
  INVENTORY_DISABLED: 'المخزون غير مفعّل فى الإعدادات',
  STOCK_ITEM_UNKNOWN: 'الصنف ليس صنفًا مخزنيًا',
  STOCK_BACKDATED: 'التاريخ قبل آخر حركة للصنف',
  STOCK_QUANTITY: 'كمية غير صالحة',
  STOCK_DUPLICATE_ITEM: 'الصنف مكرر فى نفس الجرد',
  STOCK_COST_UNKNOWN: 'لا تكلفة معروفة للصنف — أدخل تكلفة الوحدة للزيادة',
  STOCK_COUNT_NO_DIFFERENCE: 'الجرد مطابق للدفاتر — لا فرق يُرحَّل',
  STOCK_DOCUMENT_ZERO_VALUE: 'المستند بلا قيمة',
  PERIOD_CLOSED: 'الفترة مقفلة',
};

const today = () => new Date().toISOString().slice(0, 10);
const yearStart = () => new Date().getFullYear() + '-01-01';

function messageOf(caught: unknown): string {
  if (caught instanceof ProblemError) {
    const details =
      caught.problem.issues?.map(
        (issue) =>
          (issue.lineIndex !== undefined ? 'سطر ' + (issue.lineIndex + 1) + ': ' : '') +
          (ISSUE_LABELS[issue.code] ?? issue.path ?? issue.message),
      ) ?? [];
    const head = caught.problem.detail ?? caught.problem.title;
    return head + (details.length ? ': ' + details.join('، ') : '');
  }
  return caught instanceof Error ? caught.message : String(caught);
}

/**
 * المخزون: الرصيد فى تاريخ ومطابقته مع حساب المخزون فى الأستاذ، وكارت الصنف، والجرد،
 * والرصيد الافتتاحى. كل رقم هنا من الـ API كما رُحّل — الواجهة لا تُسعّر شيئًا.
 */
export default function Inventory({
  companyId,
  currency,
  canPost,
  onPosted,
  onOpenLedger,
  onSessionLost,
}: Props) {
  const [tab, setTab] = useState<Tab>('stock');
  const [items, setItems] = useState<CatalogItemDto[]>([]);
  const [failure, setFailure] = useState<string | null>(null);

  const handle = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else setFailure(messageOf(caught));
    },
    [onSessionLost],
  );

  useEffect(() => {
    api
      .catalogItems(companyId)
      .then((rows) => setItems(rows.filter((item) => item.kind === 'stock')))
      .catch(handle);
  }, [companyId, handle]);

  const tabs: { id: Tab; label: string; show: boolean }[] = [
    { id: 'stock', label: 'الرصيد والتقييم', show: true },
    { id: 'card', label: 'كارت صنف', show: true },
    { id: 'count', label: 'جرد وتسوية', show: canPost },
    { id: 'opening', label: 'رصيد افتتاحى', show: canPost },
    { id: 'documents', label: 'مستندات المخزون', show: true },
  ];

  return (
    <div className="panel">
      <h2>المخزون</h2>
      <div className="tabs" role="tablist">
        {tabs
          .filter((entry) => entry.show)
          .map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={tab === entry.id}
              className={tab === entry.id ? 'tab active' : 'tab'}
              onClick={() => {
                setTab(entry.id);
                setFailure(null);
              }}
            >
              {entry.label}
            </button>
          ))}
      </div>
      {failure && <p className="error">{failure}</p>}
      {items.length === 0 && (
        <p className="sub">لا أصناف مخزنية بعد — أضفها من «الأصناف والخدمات» بنوع «صنف مخزنى».</p>
      )}
      {tab === 'stock' && (
        <StockTab
          companyId={companyId}
          currency={currency}
          onOpenLedger={onOpenLedger}
          onError={handle}
        />
      )}
      {tab === 'card' && <CardTab companyId={companyId} items={items} onError={handle} />}
      {tab === 'count' && canPost && (
        <CountTab
          companyId={companyId}
          items={items}
          onPosted={onPosted}
          onError={handle}
          onClearError={() => setFailure(null)}
        />
      )}
      {tab === 'opening' && canPost && (
        <OpeningTab
          companyId={companyId}
          items={items}
          onPosted={onPosted}
          onError={handle}
          onClearError={() => setFailure(null)}
        />
      )}
      {tab === 'documents' && <DocumentsTab companyId={companyId} onError={handle} />}
    </div>
  );
}

function StockTab({
  companyId,
  currency,
  onOpenLedger,
  onError,
}: {
  companyId: string;
  currency: string;
  onOpenLedger: (accountCode: string) => void;
  onError: (caught: unknown) => void;
}) {
  const [asOf, setAsOf] = useState(today());
  const [stock, setStock] = useState<StockOnHandResponse | null>(null);
  const [valuation, setValuation] = useState<StockValuationResponse | null>(null);

  const load = useCallback(() => {
    Promise.all([api.stockOnHand(companyId, asOf), api.stockValuation(companyId, asOf)])
      .then(([onHand, value]) => {
        setStock(onHand);
        setValuation(value);
      })
      .catch(onError);
  }, [companyId, asOf, onError]);

  useEffect(load, [load]);

  return (
    <>
      <div className="form-row">
        <label>
          فى تاريخ
          <input type="date" value={asOf} onChange={(event) => setAsOf(event.target.value)} />
          <Hint>يعرض الكميات كما كانت فى نهاية هذا اليوم.</Hint>
        </label>
      </div>
      {stock && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>الصنف</th>
                <th>الوحدة</th>
                <th>الكمية</th>
                <th>متوسط التكلفة</th>
                <th>القيمة</th>
                <th>حساب المخزون</th>
              </tr>
            </thead>
            <tbody>
              {stock.items.map((row) => (
                <tr key={row.itemId}>
                  <td>
                    {row.nameAr}
                    {!row.isActive && <span className="sub"> (موقوف)</span>}
                  </td>
                  <td>{row.unit}</td>
                  <td className="num-cell">{formatQuantity(row.quantity)}</td>
                  <td className="num-cell">{formatMoney(row.averageCost)}</td>
                  <td className="num-cell">{formatMoney(row.value)}</td>
                  <td className="mono">{row.inventoryAccountCode}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>الإجمالى</td>
                <td className="num-cell">
                  {formatMoney(stock.totalValue)} {currency}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {valuation && (
        <>
          <h3>المطابقة مع الأستاذ</h3>
          <p className={valuation.balanced ? 'sub' : 'error'}>
            {valuation.balanced
              ? 'قيمة المخزون تطابق رصيد حسابات المخزون فى الأستاذ.'
              : 'فرق بين قيمة المخزون ورصيد الأستاذ: ' +
                formatMoney(valuation.difference) +
                ' ' +
                currency +
                ' — راجع القيود المرحّلة على حساب المخزون من خارج مستنداته.'}
          </p>
          {valuation.accounts.length > 0 && (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>الحساب</th>
                    <th>قيمة المخزون</th>
                    <th>رصيد الأستاذ</th>
                    <th>الفرق</th>
                  </tr>
                </thead>
                <tbody>
                  {valuation.accounts.map((row) => (
                    <tr
                      key={row.accountCode}
                      className="clickable"
                      tabIndex={0}
                      onClick={() => onOpenLedger(row.accountCode)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') onOpenLedger(row.accountCode);
                      }}
                    >
                      <td>
                        <span className="mono">{row.accountCode}</span> — {row.nameAr}
                      </td>
                      <td className="num-cell">{formatMoney(row.stockValue)}</td>
                      <td className="num-cell">{formatMoney(row.ledgerBalance)}</td>
                      <td className="num-cell">{formatMoney(row.difference)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

function CardTab({
  companyId,
  items,
  onError,
}: {
  companyId: string;
  items: readonly CatalogItemDto[];
  onError: (caught: unknown) => void;
}) {
  const [itemId, setItemId] = useState('');
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(today());
  const [card, setCard] = useState<StockCardResponse | null>(null);
  const [prices, setPrices] = useState<LegacyItemPricesResponse | null>(null);
  const itemName = items.find((item) => item.id === itemId)?.nameAr ?? '';

  // أسعار الصنف فى البرنامج القديم — بنفس الاسم اللى اترحّل بيه
  useEffect(() => {
    setPrices(null);
    if (!itemName) return;
    legacyApi.itemPrices(companyId, itemName).then(setPrices).catch(onError);
  }, [companyId, itemName, onError]);

  useEffect(() => {
    if (!itemId && items[0]) setItemId(items[0].id);
  }, [items, itemId]);

  useEffect(() => {
    if (!itemId || !from || !to) return;
    api.stockCard(companyId, itemId, from, to).then(setCard).catch(onError);
  }, [companyId, itemId, from, to, onError]);

  return (
    <>
      <div className="form-row">
        <label className="grow">
          الصنف
          <SearchSelect
            value={itemId}
            onChange={setItemId}
            emptyLabel="— اختر صنفًا —"
            placeholder="دوّر باسم الصنف"
            options={items.map((item) => ({ value: item.id, label: item.nameAr }))}
          />
        </label>
        <label>
          من
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          إلى
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
      </div>
      {card && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>التاريخ</th>
                <th>الحركة</th>
                <th>المستند</th>
                <th>وارد</th>
                <th>منصرف</th>
                <th>القيمة</th>
                <th>الرصيد</th>
                <th>قيمة الرصيد</th>
                <th>المتوسط</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={6}>رصيد أول المدة</td>
                <td className="num-cell">{formatQuantity(card.opening.quantity)}</td>
                <td className="num-cell">{formatMoney(card.opening.value)}</td>
                <td />
              </tr>
              {card.movements.map((row) => (
                <tr key={row.id}>
                  <td>{row.date}</td>
                  <td>{KIND_LABELS[row.kind]}</td>
                  <td className="mono">{row.reference}</td>
                  <td className="num-cell">
                    {row.quantityIn === '0' ? '' : formatQuantity(row.quantityIn)}
                  </td>
                  <td className="num-cell">
                    {row.quantityOut === '0' ? '' : formatQuantity(row.quantityOut)}
                  </td>
                  <td className="num-cell">{formatMoney(row.value)}</td>
                  <td className="num-cell">{formatQuantity(row.balanceQuantity)}</td>
                  <td className="num-cell">{formatMoney(row.balanceValue)}</td>
                  <td className="num-cell">{formatMoney(row.averageCost)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6}>رصيد آخر المدة</td>
                <td className="num-cell">{formatQuantity(card.closing.quantity)}</td>
                <td className="num-cell">{formatMoney(card.closing.value)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {prices && prices.rows.length > 0 && (
        <>
          <h3>أسعاره فى البرنامج القديم</h3>
          <p className="sub">
            {prices.lastSalePrice && <>آخر سعر بيع: {formatMoney(prices.lastSalePrice)} </>}
            {prices.lastPurchasePrice && (
              <>— آخر سعر شراء: {formatMoney(prices.lastPurchasePrice)}</>
            )}
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>المستند</th>
                  <th>رقمه</th>
                  <th>الطرف</th>
                  <th>الكمية</th>
                  <th>سعر الوحدة</th>
                  <th>الإجمالى</th>
                </tr>
              </thead>
              <tbody>
                {prices.rows.map((row, index) => (
                  <tr key={row.documentId + ':' + index}>
                    <td>{row.documentDate}</td>
                    <td>{LEGACY_DOCUMENT_LABELS[row.type]}</td>
                    <td className="mono">{row.reference ?? ''}</td>
                    <td>{row.partyName ?? ''}</td>
                    <td className="num-cell">{row.quantity ? formatQuantity(row.quantity) : ''}</td>
                    <td className="num-cell">{row.unitPrice ? formatMoney(row.unitPrice) : ''}</td>
                    <td className="num-cell">{row.total ? formatMoney(row.total) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

interface CountLine {
  itemId: string;
  quantity: string;
  unitCost: string;
}

/** نموذج سطور مستند مخزون — مشترك بين الجرد (كمية معدودة) والافتتاحى (كمية وتكلفة). */
function useLines(items: readonly CatalogItemDto[]) {
  const [lines, setLines] = useState<CountLine[]>([]);
  useEffect(() => {
    if (lines.length === 0 && items[0]) {
      setLines([{ itemId: items[0].id, quantity: '', unitCost: '' }]);
    }
  }, [items, lines.length]);
  const update = (index: number, patch: Partial<CountLine>) =>
    setLines((current) => current.map((line, at) => (at === index ? { ...line, ...patch } : line)));
  const add = () =>
    setLines((current) => [...current, { itemId: items[0]?.id ?? '', quantity: '', unitCost: '' }]);
  const remove = (index: number) =>
    setLines((current) => current.filter((_line, at) => at !== index));
  return { lines, setLines, update, add, remove };
}

function CountTab({
  companyId,
  items,
  onPosted,
  onError,
  onClearError,
}: {
  companyId: string;
  items: readonly CatalogItemDto[];
  onPosted: () => void;
  onError: (caught: unknown) => void;
  onClearError: () => void;
}) {
  const [date, setDate] = useState(today());
  const [reason, setReason] = useState('');
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<StockDocumentDto | null>(null);
  const [book, setBook] = useState<StockOnHandResponse | null>(null);
  const { lines, setLines, update, add, remove } = useLines(items);

  useEffect(() => {
    api.stockOnHand(companyId, date).then(setBook).catch(onError);
  }, [companyId, date, onError]);
  const bookOf = useMemo(
    () => new Map((book?.items ?? []).map((row) => [row.itemId, row.quantity])),
    [book],
  );

  const parsed = lines.map((line) => ({
    itemId: line.itemId,
    countedQuantity: parseAmount(line.quantity),
    unitCost: line.unitCost.trim() === '' ? undefined : parseAmount(line.unitCost),
  }));
  const ready =
    reason.trim().length >= 3 &&
    lines.length > 0 &&
    // خانة فارغة ليست صفرًا هنا: العد صفرًا يُدخل صراحةً، لا يُفترض من سهو
    lines.every((line) => line.quantity.trim() !== '') &&
    parsed.every((line) => line.itemId && line.countedQuantity !== null && line.unitCost !== null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready) return;
    setSaving(true);
    onClearError();
    try {
      const document = await api.createStockAdjustment(companyId, key, {
        documentDate: date,
        reason: reason.trim(),
        lines: parsed.map((line) => ({
          itemId: line.itemId,
          countedQuantity: line.countedQuantity!,
          ...(line.unitCost ? { unitCost: line.unitCost } : {}),
        })),
      });
      setResult(document);
      setLines([]);
      setReason('');
      setKey(crypto.randomUUID());
      onPosted();
    } catch (caught) {
      onError(caught);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <p className="sub">
        أدخل الكمية الفعلية بعد العد. الفرق عن الرصيد الدفترى يُرحَّل بتكلفة طريقة الشركة على حساب
        «فروق جرد مخزون» (التصنيف ينتظر اعتماد المحاسب).
      </p>
      <div className="form-row">
        <label>
          تاريخ الجرد
          <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          <Hint>يوم العدّ الفعلى للبضاعة على الرف.</Hint>
        </label>
        <label className="grow">
          السبب
          <input
            value={reason}
            maxLength={500}
            onChange={(event) => {
              setReason(event.target.value);
              setKey(crypto.randomUUID());
            }}
          />
          <Hint>لماذا تختلف الكمية: تلف، فقد، أو خطأ فى العدّ.</Hint>
        </label>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>الصنف</th>
              <th>الرصيد الدفترى</th>
              <th>الكمية المعدودة</th>
              <th>تكلفة الوحدة للزيادة (اختيارى)</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={index}>
                <td>
                  <select
                    value={line.itemId}
                    onChange={(event) => {
                      update(index, { itemId: event.target.value });
                      setKey(crypto.randomUUID());
                    }}
                  >
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.nameAr}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="num-cell">{formatQuantity(bookOf.get(line.itemId) ?? '0')}</td>
                <td>
                  <input
                    inputMode="decimal"
                    value={line.quantity}
                    onChange={(event) => {
                      update(index, { quantity: event.target.value });
                      setKey(crypto.randomUUID());
                    }}
                  />
                </td>
                <td>
                  <input
                    inputMode="decimal"
                    value={line.unitCost}
                    onChange={(event) => {
                      update(index, { unitCost: event.target.value });
                      setKey(crypto.randomUUID());
                    }}
                  />
                </td>
                <td>
                  <button type="button" className="link" onClick={() => remove(index)}>
                    حذف
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="form-actions sticky">
        <button type="button" className="ghost" onClick={add} disabled={items.length === 0}>
          + صنف
        </button>
        <button type="submit" className="primary" disabled={saving || !ready}>
          {saving ? 'جارى الترحيل…' : 'ترحيل الجرد'}
        </button>
        <Hint block>يجعل كميات البرنامج مثل ما عددته على الرف، ويسجّل قيمة الفرق فى الحسابات.</Hint>
      </div>
      {result && (
        <p className="sub">
          رُحّل {result.number} — قيد {result.journalNumber} — صافى الأثر{' '}
          {formatMoney(result.totalValue)}
        </p>
      )}
    </form>
  );
}

function OpeningTab({
  companyId,
  items,
  onPosted,
  onError,
  onClearError,
}: {
  companyId: string;
  items: readonly CatalogItemDto[];
  onPosted: () => void;
  onError: (caught: unknown) => void;
  onClearError: () => void;
}) {
  const [date, setDate] = useState(yearStart());
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<StockDocumentDto | null>(null);
  const { lines, setLines, update, add, remove } = useLines(items);

  const parsed = lines.map((line) => ({
    itemId: line.itemId,
    quantity: parseAmount(line.quantity),
    unitCost: parseAmount(line.unitCost),
  }));
  const ready =
    lines.length > 0 &&
    parsed.every(
      (line) =>
        line.itemId && line.quantity !== null && !isZero(line.quantity) && line.unitCost !== null,
    );

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready) return;
    setSaving(true);
    onClearError();
    try {
      const document = await api.createStockOpening(companyId, key, {
        documentDate: date,
        lines: parsed.map((line) => ({
          itemId: line.itemId,
          quantity: line.quantity!,
          unitCost: line.unitCost!,
        })),
      });
      setResult(document);
      setLines([]);
      setKey(crypto.randomUUID());
      onPosted();
    } catch (caught) {
      onError(caught);
    } finally {
      setSaving(false);
    }
  }

  const change = (index: number, patch: Partial<CountLine>) => {
    update(index, patch);
    setKey(crypto.randomUUID());
  };

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <p className="sub">
        رصيد المخزون عند بداية العمل على البرنامج: الكمية وتكلفة الوحدة لكل صنف. لا تُدخل حساب
        المخزون فى قيد الافتتاح العام — المقابل هنا حساب فروق الأرصدة الافتتاحية (أو الأرباح المرحلة
        حسب الإعدادات)، فيلتقى القيدان.
      </p>
      <div className="form-row">
        <label>
          التاريخ
          <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </label>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>الصنف</th>
              <th>الكمية</th>
              <th>تكلفة الوحدة</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={index}>
                <td>
                  <select
                    value={line.itemId}
                    onChange={(event) => change(index, { itemId: event.target.value })}
                  >
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.nameAr}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    inputMode="decimal"
                    value={line.quantity}
                    onChange={(event) => change(index, { quantity: event.target.value })}
                  />
                </td>
                <td>
                  <input
                    inputMode="decimal"
                    value={line.unitCost}
                    onChange={(event) => change(index, { unitCost: event.target.value })}
                  />
                </td>
                <td>
                  <button type="button" className="link" onClick={() => remove(index)}>
                    حذف
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="form-actions">
        <button type="button" className="ghost" onClick={add} disabled={items.length === 0}>
          + صنف
        </button>
        <button type="submit" className="primary" disabled={saving || !ready}>
          {saving ? 'جارى الترحيل…' : 'ترحيل الرصيد الافتتاحى'}
        </button>
        <Hint block>
          يسجّل البضاعة التى كانت عندك يوم بدأت على البرنامج. بعد الترحيل لا يُعدَّل ولا يُحذف —
          الخطأ يُصحَّح بمستند عكسى، حتى يبقى تاريخ كل مبلغ واضحًا.
        </Hint>
      </div>
      {result && (
        <p className="sub">
          رُحّل {result.number} — قيد {result.journalNumber} — القيمة{' '}
          {formatMoney(result.totalValue)}
        </p>
      )}
    </form>
  );
}

function DocumentsTab({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (caught: unknown) => void;
}) {
  const [documents, setDocuments] = useState<StockDocumentDto[] | null>(null);

  useEffect(() => {
    api.stockDocuments(companyId).then(setDocuments).catch(onError);
  }, [companyId, onError]);

  if (documents === null) return <Loading />;
  if (documents.length === 0) return <p className="sub">لا مستندات مخزون بعد.</p>;
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>الرقم</th>
            <th>النوع</th>
            <th>التاريخ</th>
            <th>البيان</th>
            <th>الأصناف</th>
            <th>صافى القيمة</th>
            <th>القيد</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((document) => (
            <tr key={document.id}>
              <td className="mono">{document.number}</td>
              <td>{document.kind === 'opening' ? 'رصيد افتتاحى' : 'جرد وتسوية'}</td>
              <td>{document.documentDate}</td>
              <td>{document.reason ?? '—'}</td>
              <td>
                {document.lines
                  .map((line) => line.itemName + ' (' + formatQuantity(line.quantity) + ')')
                  .join('، ')}
              </td>
              <td className="num-cell">{formatMoney(document.totalValue)}</td>
              <td className="mono">{document.journalNumber ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
